import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  getCourseBySlug, 
  formatCourseDuration, 
  formatDifficultyLevel,
  type ElearningCourse,
  getPopularCourses
} from '../../services/elearningCourseService';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import CourseReviews from '../../components/elearning/CourseReviews';
import CourseReviewForm from '../../components/elearning/CourseReviewForm';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { MiniCartPreview } from '../../components/elearning/MiniCartPreview';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { useStudentEnrollments } from '../../hooks/useStudentEnrollments';
import { 
  ArrowLeft, 
  ArrowRight,
  ShoppingCart, 
  Play, 
  Clock, 
  Award, 
  Users, 
  BookOpen, 
  Star, 
  CheckCircle, 
  BarChart, 
  Globe, 
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  Crown,
  Diamond,
  Gem,
  Sparkles,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Zap,
  Cloud
} from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useElearningCartActions, useElearningCartStats, courseToCartItem } from '../../stores/useElearningCartStore';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerChildren = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

const CourseDetailPageContent: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème E-learning (preview > tenant)
  const elearningTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  const { addItem, isItemInCart } = useElearningCartActions();
  const { items: cartItems } = useElearningCartStats();
  
  // Utiliser le hook pour vérifier les inscriptions de l'utilisateur
  const { isEnrolledInCourse, isLoading: isLoadingEnrollments, getCourseAction, getEnrollmentInfo } = useStudentEnrollments();
  
  const [course, setCourse] = useState<ElearningCourse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [similarCourses, setSimilarCourses] = useState<ElearningCourse[]>([]);
  const [activeTab, setActiveTab] = useState('apercu');

  // Vérifier si le cours est dans le panier
  const isInCart = course ? isItemInCart(course.id) : false;
  
  // Vérifier si l'utilisateur est inscrit au cours
  const isEnrolled = course ? isEnrolledInCourse(course.id) : false;
  
  // Récupérer le profil étudiant depuis le token OTP
  const [studentProfile, setStudentProfile] = useState<any>(null);
  
  useEffect(() => {
    const loadStudentProfile = async () => {
      try {
        const token = localStorage.getItem('elearning_student_token');
        if (token) {
          const profile = await getStudentProfileByToken(token);
          setStudentProfile(profile);
          console.log('Profil étudiant chargé:', profile);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du profil étudiant:', error);
      }
    };
    
    loadStudentProfile();
  }, []);
  
  // Logs de débogage pour le système d'avis
  useEffect(() => {
    if (course) {
      console.log('Débogage système d\'avis:', { 
        courseId: course.id,
        userId: user?.id,
        studentId: studentProfile?.id,
        isLoggedIn: !!user?.id || !!studentProfile?.id,
        isEnrolled,
        enrollmentInfo: getEnrollmentInfo(course.id),
        enrollmentId: getEnrollmentInfo(course.id)?.id || 'Non trouvé'
      });
    }
  }, [course, user, studentProfile, isEnrolled]);

  // Récupérer les détails du cours
  const fetchCourse = useCallback(async () => {
    if (!slug || !tenant?.id) return;
    
    setIsLoading(true);
    try {
      const courseData = await getCourseBySlug(tenant.id, slug);
      setCourse(courseData);
      
      // Initialiser tous les modules comme fermés par défaut
      if (courseData.modules) {
        const modulesState: Record<string, boolean> = {};
        courseData.modules.forEach(module => {
          modulesState[module.id] = false;
        });
        setExpandedModules(modulesState);
      }

      // Récupérer des cours similaires
      const popularCourses = await getPopularCourses(tenant.id, 4);
      setSimilarCourses(popularCourses.filter(c => c.id !== courseData.id).slice(0, 3));
    } catch (error) {
      console.error('Erreur lors du chargement du cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails du cours',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [slug, tenant?.id]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  // Gestionnaire pour ajouter au panier
  const handleAddToCart = () => {
    if (!course) return;
    
    addItem(courseToCartItem(course));
    
    toast({
      title: 'Cours ajouté au panier',
      description: 'Vous pouvez maintenant procéder au paiement',
    });
  };

  // Gestionnaire pour s'inscrire directement
  const handleEnrollNow = () => {
    if (!course) return;
    
    // Ajouter au panier et rediriger vers le checkout
    addItem(courseToCartItem(course));
    navigate('/elearning/checkout');
  };

  // Gestionnaire pour accéder au cours
  const handleAccessCourse = () => {
    if (!course) return;
    
    // Vérifier si le token d'authentification est présent
    const token = localStorage.getItem('elearning_student_token');
    if (!token) {
      toast({
        title: 'Authentification requise',
        description: 'Veuillez vous connecter pour accéder à ce cours',
        variant: 'destructive'
      });
      navigate('/elearning/login');
      return;
    }
    
    // Récupérer les informations d'inscription et rediriger vers le lecteur de cours
    const enrollmentInfo = getEnrollmentInfo(course.id);
    if (enrollmentInfo) {
      navigate(`/elearning/player/${enrollmentInfo.id}`);
    } else {
      // Fallback vers le tableau de bord étudiant si les informations d'inscription ne sont pas disponibles
      navigate('/elearning/student/dashboard');
    }
  };

  // Gestionnaire pour basculer l'état d'expansion d'un module
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Calculer le nombre total de leçons
  const totalLessons = course?.modules?.reduce((total, module) => 
    total + (module.lessons?.length || 0), 0) || 0;

  // Calculer le nombre de leçons gratuites
  const freeLessons = course?.modules?.reduce((total, module) => 
    total + (module.lessons?.filter(lesson => lesson.is_free)?.length || 0), 0) || 0;

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
              backgroundAttachment: 'fixed'
            }}
          >
            {elearningTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: elearningTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[elearningTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                      ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                      ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !elearningTheme.overlay.useGradient
                  ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="flex flex-col justify-center items-center h-screen relative z-10">
            <div className="relative">
              <div className="w-16 h-16 border-4 rounded-full animate-spin"
                style={{
                  borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2),
                  borderTopColor: tenant?.config?.color1 || '#fe8d17'
                }}
              ></div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                style={{ 
                  borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                  animationDirection: 'reverse',
                  animationDuration: '1.5s'
                }}
              ></div>
            </div>
            <span className="mt-6 text-xl font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Chargement du cours...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!course) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
              backgroundAttachment: 'fixed'
            }}
          >
            {elearningTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: elearningTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[elearningTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                      ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                      ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !elearningTheme.overlay.useGradient
                  ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="container mx-auto py-12 relative z-10">
            <div className="text-center py-20">
              <BookOpen className="h-16 w-16 mx-auto mb-4" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              <h2 className="text-3xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Cours non trouvé</h2>
              <p className="mb-6" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Le cours que vous recherchez n'existe pas ou a été supprimé.</p>
              <Button 
                onClick={() => navigate('/elearning/courses')}
                className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                style={{
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                  borderColor: `${tenant?.config?.color3 || '#ffffff'}4d`,
                  boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                Voir tous les cours
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title={`${course.title} - Formation en ligne`}
        description={course.description || `Découvrez notre formation ${course.title} et développez vos compétences.`}
        keywords={`formation, cours en ligne, e-learning, ${course.tags?.join(', ') || ''}`}
      />
      
      <div className="text-white relative overflow-hidden min-h-screen">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {elearningTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: elearningTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[elearningTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                    ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                    ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !elearningTheme.overlay.useGradient
                ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {elearningTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(elearningTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[0].icon] || BookOpen, { size: elearningTheme.icons[0].size })}
            </motion.div>
          )}
          {/* Top-Right */}
          {elearningTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(elearningTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[1].icon] || Sparkles, { size: elearningTheme.icons[1].size })}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {elearningTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(elearningTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[2].icon] || Diamond, { size: elearningTheme.icons[2].size })}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {elearningTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(elearningTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {React.createElement(iconMap[elearningTheme.icons[3].icon] || Star, { size: elearningTheme.icons[3].size })}
            </motion.div>
          )}
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-32 lg:pt-36 md:pb-20 z-20">
        {/* Fil d'Ariane */}
        <div className="flex items-center text-sm mb-6" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
          <Link to="/elearning/courses" className="transition-colors" style={{ color: tenant?.config?.color4 || '#9ca3af' }}
            onMouseEnter={(e) => e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff'}
            onMouseLeave={(e) => e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af'}
          >Formations</Link>
          <span className="mx-2">/</span>
          {course.category && (
            <>
              <Link to={`/elearning/courses?categorie=${course.category.slug}`} className="transition-colors" style={{ color: tenant?.config?.color4 || '#9ca3af' }}
                onMouseEnter={(e) => e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af'}
              >
                {course.category.name}
              </Link>
              <span className="mx-2">/</span>
            </>
          )}
          <span style={{ color: tenant?.config?.color3 || '#ffffff' }}>{course.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <motion.div 
            className="lg:col-span-2 space-y-8"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            {/* En-tête du cours */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={slideInLeft}
            >
              <div className="relative">
                {/* Éléments décoratifs */}
                <motion.div 
                  className="absolute -top-4 -left-4 text-[#D4AF37]/20"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <Crown size={32} />
                </motion.div>
                <motion.div 
                  className="absolute -top-2 -right-2 text-[#D4AF37]/30"
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Star size={20} />
                </motion.div>
                
                <h1 className="text-3xl md:text-4xl font-bold mb-4 drop-shadow-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  {course.title}
                </h1>
              </div>
              
              <motion.div 
                className="flex flex-wrap items-center gap-4 mb-6"
                variants={staggerChildren}
                initial="hidden"
                animate="visible"
              >
                {course.category && (
                  <motion.div variants={scaleIn}>
                    <Badge 
                      className="bg-white/10 border backdrop-blur-sm transition-all duration-300 shadow-lg" 
                      style={{
                        borderColor: `${tenant?.config?.color3 || '#ffffff'}80`,
                        color: tenant?.config?.color2 || '#fe8d17'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      <Sparkles size={12} className="mr-1" />
                      {course.category.name}
                    </Badge>
                  </motion.div>
                )}
                
                <motion.div variants={scaleIn} className="flex items-center bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  <Clock size={16} className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  <span className="font-medium">{formatCourseDuration(course.duration_hours)}</span>
                </motion.div>
                
                <motion.div variants={scaleIn} className="flex items-center bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  <Award size={16} className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  <span className="font-medium">{formatDifficultyLevel(course.difficulty_level)}</span>
                </motion.div>
                
                <motion.div variants={scaleIn} className="flex items-center bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                  <Users size={16} className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                  <span className="font-medium">{course.enrollment_count} inscrits</span>
                </motion.div>
                
                <motion.div variants={scaleIn} className="flex items-center bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm border border-white/20">
                  <div className="flex mr-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: i * 0.1, duration: 0.5 }}
                      >
                        <Star 
                          size={16} 
                          className={i < Math.round(course.rating_average) ? "fill-current" : "text-white/30"}
                          style={i < Math.round(course.rating_average) ? { color: '#f59e0b' } : undefined}
                        />
                      </motion.div>
                    ))}
                  </div>
                  <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>({course.rating_count})</span>
                </motion.div>
              </motion.div>
              
              {course.instructor && (
                <motion.div 
                  className="flex items-center mb-6 bg-white/10 p-4 rounded-lg backdrop-blur-sm border"
                  style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}4d` }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.6 }}
                >
                  <motion.div 
                    className="w-12 h-12 rounded-full overflow-hidden mr-4 shadow-lg"
                    style={{ backgroundImage: `linear-gradient(to bottom right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})` }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {course.instructor.avatar_url ? (
                      <img 
                        src={course.instructor.avatar_url} 
                        alt={course.instructor.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                        {course.instructor.name.charAt(0)}
                      </div>
                    )}
                  </motion.div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: tenant?.config?.color2 || '#fe8d17' }}>Instructeur</p>
                    <p className="font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{course.instructor.name}</p>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Image principale du cours */}
            <motion.div 
              className="relative rounded-xl overflow-hidden aspect-video shadow-2xl border"
              style={{
                backgroundImage: `linear-gradient(to bottom right, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.3)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)})`,
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Éléments décoratifs sur l'image */}
              <motion.div 
                className="absolute top-4 right-4"
                style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4) }}
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <Diamond size={24} />
              </motion.div>
              
              {course.thumbnail_url ? (
                <img 
                  src={course.thumbnail_url} 
                  alt={course.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <BookOpen size={64} style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4) }} />
                  </motion.div>
                </div>
              )}
              
              {course.video_preview_url && (
                <motion.button 
                  onClick={() => setIsPreviewModalOpen(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-all duration-300"
                  whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
                >
                  <motion.div 
                    className="w-20 h-20 rounded-full flex items-center justify-center shadow-2xl"
                    style={{
                      backgroundImage: `linear-gradient(to bottom right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    animate={{ 
                      boxShadow: [
                        `0 0 20px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)}`,
                        `0 0 40px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.5)}`,
                        `0 0 20px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)}`
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Play size={28} className="ml-1" style={{ color: tenant?.config?.color3 || '#ffffff' }} />
                  </motion.div>
                </motion.button>
              )}
            </motion.div>

            {/* Onglets d'information */}
            <Tabs defaultValue="apercu" value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-4 mb-6 p-1 rounded-lg border" style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}4d` }}>
                <TabsTrigger 
                  value="apercu" 
                  className="data-[state=active]:text-white transition-all duration-300 rounded-lg"
                  style={{
                    color: activeTab === 'apercu' ? tenant?.config?.color3 || '#ffffff' : tenant?.config?.color3 || '#008aef',
                    backgroundColor: activeTab === 'apercu' ? tenant?.config?.color1 || '#008aef' : `${tenant?.config?.color3 || '#ffffff'}33`
                  }}
                >Aperçu</TabsTrigger>
                <TabsTrigger 
                  value="contenu" 
                  className="data-[state=active]:text-white transition-all duration-300 rounded-lg"
                  style={{
                    color: activeTab === 'contenu' ? tenant?.config?.color3 || '#ffffff' : tenant?.config?.color3 || '#008aef',
                    backgroundColor: activeTab === 'contenu' ? tenant?.config?.color1 || '#008aef' : `${tenant?.config?.color3 || '#ffffff'}33`
                  }}
                >Contenu du cours</TabsTrigger>
                <TabsTrigger 
                  value="instructeur" 
                  className="data-[state=active]:text-white transition-all duration-300 rounded-lg"
                  style={{
                    color: activeTab === 'instructeur' ? tenant?.config?.color3 || '#ffffff' : tenant?.config?.color3 || '#008aef',
                    backgroundColor: activeTab === 'instructeur' ? tenant?.config?.color1 || '#008aef' : `${tenant?.config?.color3 || '#ffffff'}33`
                  }}
                >Instructeur</TabsTrigger>
                <TabsTrigger 
                  value="avis" 
                  className="data-[state=active]:text-white transition-all duration-300 rounded-lg"
                  style={{
                    color: activeTab === 'avis' ? tenant?.config?.color3 || '#ffffff' : tenant?.config?.color3 || '#008aef',
                    backgroundColor: activeTab === 'avis' ? tenant?.config?.color1 || '#008aef' : `${tenant?.config?.color3 || '#ffffff'}33`
                  }}
                >Avis</TabsTrigger>
              </TabsList>
              
              <TabsContent value="apercu" className="space-y-6">
                <div className="bg-white/5 border backdrop-blur-sm rounded-lg p-6 shadow-xl" style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>À propos de ce cours</h2>
                  <div className="leading-relaxed whitespace-pre-line" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                    {course.description}
                  </div>
                </div>
                
                {course.learning_objectives && course.learning_objectives.length > 0 && (
                  <div className="bg-white/5 border backdrop-blur-sm rounded-lg p-6 shadow-xl" style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}>
                    <h3 className="text-xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Ce que vous apprendrez</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {course.learning_objectives.map((objective, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircle size={18} className="mr-2 mt-1 flex-shrink-0" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                          <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>{objective}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {course.requirements && course.requirements.length > 0 && (
                  <div className="bg-white/5 border backdrop-blur-sm rounded-lg p-6 shadow-xl" style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}>
                    <h3 className="text-xl font-bold mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Prérequis</h3>
                    <ul className="list-disc pl-5 space-y-2" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      {course.requirements.map((requirement, index) => (
                        <li key={index}>{requirement}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="contenu" className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Contenu du cours</h2>
                  <div className="text-sm" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                    <span>{course.modules?.length || 0} modules</span>
                    <span className="mx-2">•</span>
                    <span>{totalLessons} leçons</span>
                    <span className="mx-2">•</span>
                    <span>{formatCourseDuration(course.duration_hours)}</span>
                  </div>
                </div>
                
                {course.modules && course.modules.length > 0 ? (
                  <div className="space-y-3">
                    {course.modules.map((module, index) => (
                      <div key={module.id} className="border backdrop-blur-sm rounded-lg overflow-hidden bg-white/5 shadow-xl" style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}>
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
                          style={{ color: tenant?.config?.color3 || '#ffffff' }}
                        >
                          <div className="flex items-center">
                            <span className="mr-3" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>{index + 1}.</span>
                            <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{module.title}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm mr-3" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                              {module.lessons?.length || 0} leçons
                            </span>
                            {expandedModules[module.id] ? (
                              <ChevronUp size={18} style={{ color: tenant?.config?.color3 || '#ffffff' }} />
                            ) : (
                              <ChevronDown size={18} style={{ color: tenant?.config?.color3 || '#ffffff' }} />
                            )}
                          </div>
                        </button>
                        
                        {expandedModules[module.id] && module.lessons && (
                          <div className="border-t" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2) }}>
                            {module.lessons.map((lesson, lessonIndex) => (
                              <div 
                                key={lesson.id}
                                className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                              >
                                <div className="flex items-center">
                                  {lesson.content_type === 'video' && <Play size={16} className="mr-3" style={{ color: '#dc2626' }} />}
                                  {lesson.content_type === 'text' && <BookOpen size={16} className="mr-3" style={{ color: '#2563eb' }} />}
                                  {lesson.content_type === 'quiz' && <BarChart size={16} className="mr-3" style={{ color: '#16a34a' }} />}
                                  
                                  <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>{lesson.title}</span>
                                  
                                  {lesson.is_free && (
                                    <Badge variant="outline" className="ml-3 backdrop-blur-sm"
                                      style={{
                                        backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                                        borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.3),
                                        color: tenant?.config?.color1 || '#fe8d17'
                                      }}
                                    >Gratuit</Badge>
                                  )}
                                </div>
                                
                                <div className="text-sm" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                                  {lesson.video_duration > 0 && (
                                    <span>{Math.floor(lesson.video_duration / 60)}:{(lesson.video_duration % 60).toString().padStart(2, '0')}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="bg-white/5 backdrop-blur-sm rounded-lg p-6" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6), borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}>Aucun contenu disponible pour ce cours.</p>
                )}
              </TabsContent>
              
              <TabsContent value="instructeur" className="space-y-6">
                {course.instructor ? (
                  <>
                    <div className="flex items-center">
                      <div className="w-20 h-20 rounded-full overflow-hidden mr-5" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.8) }}>
                        {course.instructor.avatar_url ? (
                          <img 
                            src={course.instructor.avatar_url} 
                            alt={course.instructor.name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                            {course.instructor.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{course.instructor.name}</h3>
                        {course.instructor.expertise && course.instructor.expertise.length > 0 && (
                          <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>{course.instructor.expertise.join(', ')}</p>
                        )}
                      </div>
                    </div>
                    
                    {course.instructor.bio && (
                      <div className="prose prose-invert max-w-none">
                        <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="whitespace-pre-line">{course.instructor.bio}</p>
                      </div>
                    )}
                    
                    {course.instructor.social_links && Object.keys(course.instructor.social_links).length > 0 && (
                      <div className="flex gap-3">
                        {Object.entries(course.instructor.social_links).map(([platform, url]) => (
                          <a 
                            key={platform} 
                            href={url as string} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {platform}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>Aucune information disponible sur l'instructeur.</p>
                )}
              </TabsContent>
              
              <TabsContent value="avis" className="space-y-6">
                <CourseReviews 
                  courseId={course.id}
                  ratingAverage={course.rating_average}
                  ratingCount={course.rating_count}
                />
                
                {/* Afficher le formulaire si l'utilisateur est inscrit au cours */}
                {(isEnrolled || (studentProfile && getEnrollmentInfo(course.id))) && (
                  <CourseReviewForm
                    courseId={course.id}
                    studentId={studentProfile?.id || user?.id || ''}
                    enrollmentId={getEnrollmentInfo(course.id)?.id || ''}
                    onReviewSubmitted={fetchCourse}
                  />
                )}
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Colonne latérale */}
          <motion.div 
            className="space-y-6"
            initial="hidden"
            animate="visible"
            variants={slideInRight}
          >
            {/* Carte d'achat */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Card className="border rounded-xl sticky top-24 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}
              >
                {/* Élément décoratif en haut */}
                <div className="h-1" style={{ backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})` }}></div>
                
                <CardHeader className="pb-3 relative">
                  {/* Éléments décoratifs */}
                  <motion.div 
                    className="absolute top-2 right-2"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}
                  >
                    <Gem size={20} />
                  </motion.div>
                  
                  <div className="flex items-center justify-between">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
                    >
                      <CardTitle className="text-2xl font-bold"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: course.price > 0 ? undefined : `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                          backgroundClip: course.price > 0 ? undefined : 'text',
                          WebkitBackgroundClip: course.price > 0 ? undefined : 'text',
                          WebkitTextFillColor: course.price > 0 ? undefined : 'transparent'
                        }}
                      >
                        {course.price > 0 ? `${course.price.toLocaleString()} FCFA` : (
                          <span className="flex items-center" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                            <Star className="mr-1" size={20} style={{ color: '#f59e0b' }} />
                            Gratuit
                          </span>
                        )}
                      </CardTitle>
                    </motion.div>
                  </div>
                </CardHeader>
              <CardContent className="p-6 space-y-6">
                <motion.div 
                  className="space-y-4"
                  variants={staggerChildren}
                  initial="hidden"
                  animate="visible"
                >
                  {isEnrolled ? (
                    (() => {
                      const { action, label } = getCourseAction(course.id);
                      const enrollmentInfo = getEnrollmentInfo(course.id);
                      return (
                        <motion.div variants={scaleIn}>
                          <Button 
                            onClick={() => {
                              // Vérifier si le token d'authentification est présent
                              const token = localStorage.getItem('elearning_student_token');
                              if (!token) {
                                toast({
                                  title: 'Authentification requise',
                                  description: 'Veuillez vous connecter pour accéder à ce cours',
                                  variant: 'destructive'
                                });
                                navigate('/elearning/auth');
                                return;
                              }
                              
                              // Utiliser la route correcte du lecteur de cours
                              if (enrollmentInfo) {
                                navigate(`/elearning/player/${enrollmentInfo.id}`);
                              } else {
                                // Fallback vers le tableau de bord étudiant si les informations d'inscription ne sont pas disponibles
                                navigate('/elearning/student/dashboard');
                              }
                            }}
                            className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                            style={{
                              backgroundColor: '#16a34a',
                              color: '#ffffff',
                              backgroundImage: 'linear-gradient(to right, #16a34a, #15803d)',
                              borderColor: '#16a34a4d',
                              boxShadow: '0 4px 15px rgba(22, 163, 74, 0.4)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundImage = 'linear-gradient(to right, #15803d, #16a34a)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundImage = 'linear-gradient(to right, #16a34a, #15803d)';
                            }}
                          >
                            {action === "view" ? <CheckCircle className="mr-2" size={18} /> : 
                             action === "continue" ? <ArrowRight className="mr-2" size={18} /> : 
                             <Play className="mr-2" size={18} />}
                            {label}
                          </Button>
                        </motion.div>
                      );
                    })()
                  ) : (
                    <>
                      <motion.div variants={scaleIn}>
                        <Button 
                          onClick={handleEnrollNow}
                          className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold transform hover:scale-105"
                          style={{
                            backgroundColor: tenant?.config?.color1 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff',
                            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                          }}
                        >
                          <Star className="mr-2" size={18} />
                          S'inscrire maintenant
                        </Button>
                      </motion.div>
                      
                      {!isInCart && (
                        <motion.div variants={scaleIn}>
                          <Button 
                            onClick={handleAddToCart}
                            variant="outline"
                            className="w-full transition-all duration-300 transform hover:scale-105"
                            style={{
                              borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8),
                              color: tenant?.config?.color1 || '#fe8d17'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                              e.currentTarget.style.borderColor = tenant?.config?.color3 || '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8);
                            }}
                          >
                            <ShoppingCart className="mr-2" size={18} />
                            Ajouter au panier
                          </Button>
                        </motion.div>
                      )}
                      
                      {isInCart && (
                        <motion.div variants={scaleIn}>
                          <Button 
                            onClick={() => navigate('/elearning/cart')}
                            variant="outline"
                            className="w-full transition-all duration-300 transform hover:scale-105"
                            style={{
                              borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8),
                              color: tenant?.config?.color1 || '#fe8d17'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                              e.currentTarget.style.borderColor = tenant?.config?.color3 || '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8);
                            }}
                          >
                            <ShoppingCart className="mr-2" size={18} />
                            Voir le panier
                          </Button>
                        </motion.div>
                      )}
                    </>
                  )}
                </motion.div>
                
                <motion.div 
                  className="space-y-3 pt-2 border-t"
                  style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2) }}
                  variants={staggerChildren}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#16a34a', 0.1) }}>
                    <CheckCircle size={18} className="mr-3" style={{ color: '#16a34a' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">Accès à vie</span>
                  </motion.div>
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#2563eb', 0.1) }}>
                    <BookOpen size={18} className="mr-3" style={{ color: '#2563eb' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">{course.modules?.length || 0} modules</span>
                  </motion.div>
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#7c3aed', 0.1) }}>
                    <CheckCircle size={18} className="mr-3" style={{ color: '#7c3aed' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">{totalLessons} leçons</span>
                  </motion.div>
                  {freeLessons > 0 && (
                    <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1) }}>
                      <Star size={18} className="mr-3" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                      <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">{freeLessons} leçons gratuites</span>
                    </motion.div>
                  )}
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#0891b2', 0.1) }}>
                    <Globe size={18} className="mr-3" style={{ color: '#0891b2' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">Accès en ligne</span>
                  </motion.div>
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#7c2d12', 0.1) }}>
                    <Calendar size={18} className="mr-3" style={{ color: '#7c2d12' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">Mises à jour régulières</span>
                  </motion.div>
                  <motion.div variants={scaleIn} className="flex items-center p-2 rounded-lg" style={{ backgroundColor: hexToRgba('#dc2626', 0.1) }}>
                    <Award size={18} className="mr-3" style={{ color: '#dc2626' }} />
                    <span style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }} className="font-medium">Certificat de réussite</span>
                  </motion.div>
                </motion.div>
              </CardContent>
            </Card>
            </motion.div>
            
            {/* Informations complémentaires */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <Card className="border rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}
              >
                <div className="h-1" style={{ backgroundImage: `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color1 || '#fe8d17'})` }}></div>
                
                <CardHeader className="pb-3 relative">
                  <motion.div 
                    className="absolute top-2 right-2"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}
                  >
                    <Diamond size={16} />
                  </motion.div>
                  <CardTitle className="text-lg flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                    <BookOpen className="mr-2" size={20} style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                    Informations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <motion.div 
                    className="flex justify-between items-center p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.05) }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <span className="font-medium" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>Dernière mise à jour</span>
                    <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{new Date(course.updated_at).toLocaleDateString()}</span>
                  </motion.div>
                  <motion.div 
                    className="flex justify-between items-center p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: hexToRgba('#2563eb', 0.05) }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <span className="font-medium" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>Langue</span>
                    <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{course.language === 'fr' ? 'Français' : course.language}</span>
                  </motion.div>
                  <motion.div 
                    className="flex justify-between items-center p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: hexToRgba('#16a34a', 0.05) }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <span className="font-medium" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>Niveau</span>
                    <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{formatDifficultyLevel(course.difficulty_level)}</span>
                  </motion.div>
                  <motion.div 
                    className="flex justify-between items-center p-2 rounded-lg transition-colors"
                    style={{ backgroundColor: hexToRgba('#f59e0b', 0.05) }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <span className="font-medium" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>Durée</span>
                    <span className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{formatCourseDuration(course.duration_hours)}</span>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* Tags */}
            {course.tags && course.tags.length > 0 && (
              <Card className="border rounded-xl shadow-xl"
                style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}
              >
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {course.tags.map((tag, index) => (
                      <Badge 
                        key={index}
                        variant="outline" 
                        className="backdrop-blur-sm transition-all duration-300"
                        style={{
                          backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                          borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3),
                          color: tenant?.config?.color1 || '#fe8d17'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Cours similaires */}
            {similarCourses.length > 0 && (
              <Card className="border rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                style={{ borderColor: `${tenant?.config?.color3 || '#ffffff'}33` }}
              >
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Cours similaires</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  {similarCourses.map(similarCourse => (
                    <Link 
                      key={similarCourse.id}
                      to={`/elearning/courses/${similarCourse.slug}`}
                      className="flex items-center space-x-3 p-2 rounded-md transition-colors"
                      style={{
                        color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8)
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.1) }}>
                        {similarCourse.thumbnail_url ? (
                          <img 
                            src={similarCourse.thumbnail_url} 
                            alt={similarCourse.title} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen size={16} style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.3) }} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium truncate" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{similarCourse.title}</h4>
                        <div className="flex items-center text-xs" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                          <span>{formatCourseDuration(similarCourse.duration_hours)}</span>
                          <span className="mx-1">•</span>
                          <div className="flex">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star 
                                key={i} 
                                size={10} 
                                className={i < Math.round(similarCourse.rating_average) ? "fill-current" : "text-white/30"}
                                style={i < Math.round(similarCourse.rating_average) ? { color: '#f59e0b' } : undefined}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
      </div>
      
      {/* Modal de prévisualisation vidéo */}
      {isPreviewModalOpen && course.video_preview_url && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: hexToRgba('#000000', 0.8) }}>
          <div className="relative w-full max-w-4xl">
            <button 
              onClick={() => setIsPreviewModalOpen(false)}
              className="absolute -top-10 right-0 transition-opacity hover:opacity-80"
              style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}
            >
              Fermer
            </button>
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl border" style={{ backgroundColor: '#000000', borderColor: `${tenant?.config?.color3 || '#ffffff'}4d` }}>
              <iframe 
                src={course.video_preview_url} 
                className="w-full h-full" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

const CourseDetailPage: React.FC = () => {
  return (
    <>
      <MiniCartPreview />
      <CourseDetailPageContent />
    </>
  );
};

export default CourseDetailPage;
